import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { Artist } from '../../interfaces/artist';
import { artistService } from '../../services/artist';
import { composerService, type CatalogComposer } from '../../services/composers';

type ComposerProfile = {
  id: string;
  artist: Artist;
  summary: string;
  era?: string;
  birthYear?: string;
  deathYear?: string;
};

const PLACEHOLDER_IMAGE =
  'https://i.scdn.co/image/ab6761610000e5eb830c172d555a63ec6d17a87f';

const coerceYear = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value).toString();
  if (typeof value === 'string' && value.trim().length) return value.trim();
  return undefined;
};

const resolveSpotifyArtistId = (
  composer: CatalogComposer | null | undefined,
  fallback?: ComposerProfile
): string | undefined => {
  if (!composer) {
    return fallback?.artist.id;
  }
  return (
    (composer.spotifyArtistId as string | undefined) ??
    (composer.spotify_artist_id as string | undefined) ??
    (composer as any)?.spotifyId ??
    (composer as any)?.spotify_id ??
    fallback?.artist.id
  );
};

const resolveName = (composer: CatalogComposer | null | undefined, fallback?: ComposerProfile) => {
  if (composer?.name && typeof composer.name === 'string') return composer.name;
  const candidate =
    (composer as any)?.displayName ??
    (composer as any)?.display_name ??
    (composer as any)?.sortableName ??
    (composer as any)?.sortable_name;
  if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  return fallback?.artist.name ?? 'Unknown Composer';
};

const resolveImageUrl = (composer: CatalogComposer | null | undefined, fallback?: ComposerProfile) => {
  if (composer && typeof (composer as any)?.imageUrl === 'string' && (composer as any).imageUrl) {
    return (composer as any).imageUrl as string;
  }
  const primaryImage = (composer as any)?.primaryImage ?? (composer as any)?.primary_image;
  if (primaryImage && typeof primaryImage.url === 'string') {
    return primaryImage.url;
  }
  if (Array.isArray((composer as any)?.images)) {
    const firstWithUrl = (composer as any).images.find((img: unknown) => typeof (img as any)?.url === 'string');
    if (firstWithUrl?.url) {
      return firstWithUrl.url as string;
    }
  }
  const fallbackImage = fallback?.artist.images?.[0]?.url;
  if (fallbackImage) return fallbackImage;
  return PLACEHOLDER_IMAGE;
};

const resolveSummary = (composer: CatalogComposer | null | undefined, fallback?: ComposerProfile) => {
  if (!composer) return fallback?.summary ?? '';
  const summaryCandidate =
    (composer.summary as string | undefined) ??
    (composer.bio as string | undefined) ??
    (composer.description as string | undefined) ??
    (composer as any)?.shortBio ??
    (composer as any)?.short_bio;
  if (typeof summaryCandidate === 'string') return summaryCandidate;
  return fallback?.summary ?? '';
};

const buildArtistProfile = ({
  spotifyId,
  name,
  imageUrl,
  followers,
  genres,
  popularity,
}: {
  spotifyId: string;
  name: string;
  imageUrl: string;
  followers: number;
  genres?: string[];
  popularity?: number;
}): Artist => ({
  external_urls: { spotify: `https://open.spotify.com/artist/${spotifyId}` },
  followers: { href: '', total: followers },
  genres: genres ?? ['classical'],
  href: `https://api.spotify.com/v1/artists/${spotifyId}`,
  id: spotifyId,
  images: [
    { url: imageUrl, width: 640, height: 640 },
    { url: imageUrl, width: 320, height: 320 },
    { url: imageUrl, width: 160, height: 160 },
  ],
  name,
  popularity: popularity ?? 80,
  type: 'artist',
  uri: `spotify:artist:${spotifyId}`,
});

const fallbackProfilesSource = [
  {
    composerId: 'cmf-composer-bach',
    spotifyId: '5aIqB5nVVvmFsvSdExz408',
    name: 'Johann Sebastian Bach',
    imageUrl: 'https://i.scdn.co/image/ab6761610000e5eb830c172d555a63ec6d17a87f',
    followers: 4382197,
    genres: ['baroque', 'german classical'],
    popularity: 79,
    era: 'Baroque',
    birthYear: '1685',
    deathYear: '1750',
    summary:
      'Cantor of Leipzig known for sacred vocal works, organ masterpieces, and intricate counterpoint.',
  },
  {
    composerId: 'cmf-composer-beethoven',
    spotifyId: '2wOqMjp9TyABvtHdOSOTUS',
    name: 'Ludwig van Beethoven',
    imageUrl: 'https://i.scdn.co/image/ab6761610000e5eba4721517972ed18f5d9413a4',
    followers: 5704358,
    genres: ['classical', 'german classical'],
    popularity: 84,
    era: 'Classical / Early Romantic',
    birthYear: '1770',
    deathYear: '1827',
    summary:
      'Revolutionary symphonist whose music bridged Classical clarity with Romantic expression.',
  },
  {
    composerId: 'cmf-composer-holst',
    spotifyId: '3MKCzCnpzw3TjUYs2v7vDA',
    name: 'Gustav Holst',
    imageUrl: 'https://i.scdn.co/image/ab6761610000e5ebb91604320f27463e99e36fc5',
    followers: 604221,
    genres: ['classical', 'english classical'],
    popularity: 64,
    era: '20th Century',
    birthYear: '1874',
    deathYear: '1934',
    summary:
      'English composer best known for The Planets and innovative choral writing rooted in folk traditions.',
  },
];

const FALLBACK_BY_COMPOSER_ID = new Map<string, ComposerProfile>();
const FALLBACK_BY_SPOTIFY_ID = new Map<string, ComposerProfile>();

for (const entry of fallbackProfilesSource) {
  const artist = buildArtistProfile({
    spotifyId: entry.spotifyId,
    name: entry.name,
    imageUrl: entry.imageUrl,
    followers: entry.followers,
    genres: entry.genres,
    popularity: entry.popularity,
  });
  const profile: ComposerProfile = {
    id: entry.composerId,
    artist,
    summary: entry.summary,
    era: entry.era,
    birthYear: entry.birthYear,
    deathYear: entry.deathYear,
  };
  FALLBACK_BY_COMPOSER_ID.set(entry.composerId, profile);
  FALLBACK_BY_SPOTIFY_ID.set(entry.spotifyId, profile);
}

const getFallbackProfile = (identifier: string): ComposerProfile | undefined => {
  if (!identifier) return undefined;
  if (identifier.startsWith('spotify:artist:')) {
    const spotifyId = identifier.split(':').pop();
    if (spotifyId) return FALLBACK_BY_SPOTIFY_ID.get(spotifyId);
  }
  return (
    FALLBACK_BY_SPOTIFY_ID.get(identifier) ??
    FALLBACK_BY_COMPOSER_ID.get(identifier)
  );
};

const mapComposerToProfile = (
  composer: CatalogComposer | null,
  artist: Artist,
  fallback: ComposerProfile | undefined,
  spotifyArtistId: string
): ComposerProfile => {
  const id = (composer?.id as string | undefined) ?? fallback?.id ?? spotifyArtistId;

  return {
    id,
    artist,
    summary: resolveSummary(composer, fallback),
    era: (composer?.era as string | undefined) ?? (composer?.period as string | undefined) ?? fallback?.era,
    birthYear: coerceYear(
      composer?.birthYear ??
        (composer as any)?.birth_year ??
        (composer as any)?.birth ??
        (composer as any)?.birthYearStart
    ) ?? fallback?.birthYear,
    deathYear: coerceYear(
      composer?.deathYear ??
        (composer as any)?.death_year ??
        (composer as any)?.death ??
        (composer as any)?.deathYearEnd
    ) ?? fallback?.deathYear,
  };
};

const ensureArtist = async (
  spotifyArtistId: string,
  composer: CatalogComposer | null,
  fallback: ComposerProfile | undefined
): Promise<Artist> => {
  try {
    const response = await artistService.fetchArtist(spotifyArtistId);
    if (response?.data) {
      return response.data;
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[composerSlice] Spotify artist fetch failed, using fallback profile', error);
    }
  }

  const baseImage = resolveImageUrl(composer, fallback);
  const followers =
    (composer as any)?.followers ??
    (composer as any)?.followersCount ??
    fallback?.artist.followers.total ??
    0;
  const genres =
    (composer as any)?.genres ??
    (composer as any)?.genre ??
    fallback?.artist.genres ??
    ['classical'];
  const popularity =
    (composer as any)?.popularity ??
    fallback?.artist.popularity ??
    70;

  return buildArtistProfile({
    spotifyId: spotifyArtistId,
    name: resolveName(composer, fallback),
    imageUrl: baseImage,
    followers: typeof followers === 'number' ? followers : Number(followers) || 0,
    genres: Array.isArray(genres) ? (genres as string[]) : fallback?.artist.genres,
    popularity: typeof popularity === 'number' ? popularity : Number(popularity) || 70,
  });
};

export const loadComposer = createAsyncThunk<ComposerProfile, string>(
  'composer/load',
  async (identifier: string) => {
    const fallback = getFallbackProfile(identifier);
    const normalizedIdentifier = identifier.startsWith('spotify:artist:')
      ? identifier.split(':').pop() ?? identifier
      : identifier;

    const potentialSpotifyIdFromParam = identifier.startsWith('spotify:artist:')
      ? identifier.split(':').pop() ?? undefined
      : undefined;
    const looksLikeSpotifyId =
      typeof normalizedIdentifier === 'string' &&
      /^[0-9A-Za-z]{16,}$/.test(normalizedIdentifier) &&
      !normalizedIdentifier.includes('-');

    let composer: CatalogComposer | null = null;
    // Only try catalog ID lookup if it doesn't look like a Spotify ID (catalog IDs are UUIDs with dashes)
    if (!looksLikeSpotifyId) {
      composer = await composerService.fetchById(normalizedIdentifier);
    }
    // If not found and it looks like a Spotify ID, or if catalog lookup failed, try Spotify ID lookup
    if (!composer) {
      composer = await composerService.fetchBySpotifyId(normalizedIdentifier);
    }

    const spotifyArtistId =
      resolveSpotifyArtistId(composer, fallback) ??
      potentialSpotifyIdFromParam ??
      (looksLikeSpotifyId ? normalizedIdentifier : undefined) ??
      fallback?.artist.id;

    if (!spotifyArtistId) {
      throw new Error('Unable to resolve composer Spotify ID');
    }

    const artist = await ensureArtist(spotifyArtistId, composer, fallback);
    return mapComposerToProfile(composer, artist, fallback, spotifyArtistId);
  }
);

interface ComposerState {
  profile: ComposerProfile | null;
  loading: boolean;
}

const initialState: ComposerState = {
  profile: null,
  loading: false,
};

const composerSlice = createSlice({
  name: 'composer',
  initialState,
  reducers: {
    resetComposer(state) {
      state.profile = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadComposer.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadComposer.fulfilled, (state, action) => {
      state.profile = action.payload;
      state.loading = false;
    });
    builder.addCase(loadComposer.rejected, (state, action) => {
      const fallback = getFallbackProfile(action.meta.arg);
      state.profile = fallback ?? null;
      state.loading = false;
    });
  },
});

export const composerActions = {
  ...composerSlice.actions,
  loadComposer,
};

export default composerSlice.reducer;
